import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { Subject } from 'rxjs';
import { CheckoutSessionService } from 'src/app/features/events/services/checkout-session.service';
import MobileOs from '../../enums/mobile-os';
import getMobileOS from '../../functions/getMobileOS';
import { ApplePayToken } from '../../types/applePayResponse.type';
import { GooglePayResponse } from '../../types/googlePayResponse.type';

export type LoadPaymentDataInfo =
  | {
      wallet: 'google';
      data: GooglePayResponse;
    }
  | { wallet: 'apple'; data: { token: ApplePayToken } };

@Component({
  selector: 'app-pay-with-wallet',
  templateUrl: './pay-with-wallet.component.html',
  styleUrls: ['./pay-with-wallet.component.css'],
})
export class PayWithWalletComponent implements OnInit, AfterViewInit {
  @Input()
  amount: string = '0.00';

  @Input()
  label: string = 'Demo (Card is not charged)';

  @Input()
  type: string = 'final';

  @Input()
  paymentStatus: Subject<{ authorized: boolean }> = new Subject();

  @Output() public loadPaymentData = new EventEmitter<LoadPaymentDataInfo>();

  private applePaymentRequest = {
    countryCode: 'DO',
    currencyCode: 'DOP',
    merchantCapabilities: ['supports3DS'],
    supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
    requiredShippingContactFields: ['name', 'email'],
    total: {
      label: this.label,
      type: this.type,
      amount: this.amount,
    },
  };

  public styleInGooglePay = {
    width: '300px',
    height: '40px',
  };

  get googlePaymentRequest() {
    const data = {
      apiVersion: 2,
      apiVersionMinor: 0,
      emailRequired: true,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['VISA', 'MASTERCARD'],
            billingAddressRequired: true,
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: 'pagosazul',
              gatewayMerchantId: '39941490002',
            },
          },
        },
      ],
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPriceLabel: this.label,
        totalPrice: this.amount,
        currencyCode: 'DOP',
        countryCode: 'DO',
      },
      merchantInfo: {
        merchantName: 'Tix',
        merchantId: 'BCR2DN7TRC77VZQE',
      },
    } as any;

    return data;
  }

  mobileOS!: MobileOs | null;
  mobileOSAndroid = MobileOs.Android;
  mobileOSIOS = MobileOs.IOS;

  constructor(private checkoutService: CheckoutSessionService) {}

  ngOnInit(): void {
    this.mobileOS = getMobileOS();
  }

  ngAfterViewInit(): void {
    if ((window as any).ApplePaySession.canMakePayments()) {
      this.createApplePayButton();
    }
  }

  onLoadPaymentData(data: any) {
    this.loadPaymentData.emit({ wallet: 'google', data: data.detail });
  }

  async onApplePayButtonClickedPaymentRequest() {
    // Consider falling back to Apple Pay JS if Payment Request is not available.
    if (!PaymentRequest) {
      return;
    }

    try {
      // Define PaymentMethodData
      const paymentMethodData = [
        {
          supportedMethods: 'https://apple.com/apple-pay',
          data: {
            version: 3,
            merchantIdentifier: 'platformintegrator.pruebas.azul.apple.pay',
            merchantCapabilities: ['supports3DS'],
            supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
            countryCode: 'DO',
          },
        },
      ];
      // Define PaymentDetails
      const paymentDetails = {
        total: {
          label: this.label,
          amount: {
            value: this.amount,
            currency: 'DOP',
          },
        },
      };
      // Define PaymentOptions
      const paymentOptions = {
        requestPayerName: true,
        requestPayerEmail: true,
        shippingType: 'shipping',
      };

      // Create PaymentRequest
      const request = new (PaymentRequest as any)(
        paymentMethodData,
        paymentDetails,
        paymentOptions
      );

      request.onmerchantvalidation = async (event: any) => {
        // Call your own server to request a new merchant session.
        const merchantSessionPromise =
          (await this.checkoutService.validateMerchant()) as any;

        console.log(merchantSessionPromise.ApplePayPaymentSessionDataJSON);
        event.complete(merchantSessionPromise.ApplePayPaymentSessionDataJSON);
      };

      const response = await request.show();
      console.log('RESPONSE: ', response);
      const status = 'success';
      await response.complete(status);
    } catch (e) {
      console.log('ERROR PAYMENT REQUEST: ', e);
      // Handle errors
    }
  }

  async onApplePayButtonClicked() {
    const ApplePaySession = (window as any).ApplePaySession;

    if (!ApplePaySession) {
      return;
    }

    this.applePaymentRequest.total = {
      label: this.label,
      type: this.type,
      amount: this.amount,
    };

    const session = new ApplePaySession(3, this.applePaymentRequest);

    session.onvalidatemerchant = async () => {
      const resp = (await this.checkoutService.validateMerchant()) as any;
      const merchantSession = JSON.parse(resp.ApplePayPaymentSessionDataJSON);
      session.completeMerchantValidation(merchantSession);
    };

    session.onpaymentauthorized = (token: ApplePayToken) => {
      this.loadPaymentData.emit({ wallet: 'apple', data: { token } });

      let timeout: any;

      const subs = this.paymentStatus.subscribe(({ authorized }) => {
        const result = {
          status: authorized
            ? ApplePaySession.STATUS_SUCCESS
            : ApplePaySession.STATUS_FAILURE,
        };

        session.completePayment(result);
        clearTimeout(timeout);
      });

      timeout = setTimeout(() => {
        subs.unsubscribe();
      }, 1000 * 30);
    };

    session.oncancel = () => {
      console.log('Payment canceled');
    };

    session.begin();
  }

  private createApplePayButton(): void {
    const button = document.createElement('apple-pay-button');
    button.setAttribute('buttonstyle', 'black');
    button.setAttribute('type', 'plain');
    button.setAttribute('locale', 'es-MX');

    // Aplicar estilos responsive
    this.updateApplePayButtonSize(button);

    button.addEventListener('click', () => {
      this.onApplePayButtonClicked();
    });

    const container = document.getElementById('apple-pay-button-container');
    if (container) container.appendChild(button);

    // Agregar listener para cambios de tamaño de ventana
    window.addEventListener('resize', () => {
      this.updateApplePayButtonSize(button);
    });
  }

  private updateApplePayButtonSize(button: HTMLElement): void {
    const height = '40px';

    // Use full width and consistent height across breakpoints to match Google Pay
    button.style.setProperty('--apple-pay-button-width', '100%');
    button.style.setProperty('--apple-pay-button-height', height);
    button.style.setProperty('--apple-pay-button-padding', '0');

    // Apply large pill radius
    button.style.setProperty('--apple-pay-button-border-radius', '100px');
    button.style.setProperty('--apple-pay-button-box-sizing', 'border-box');
  }
}
